import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft, FileDown, IdCard, GraduationCap, ClipboardList, FileSignature,
    CreditCard, FileText, MessageSquareWarning, X, CheckCircle2, Clock, Mail,
    AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";

const TABS = [
    { key: "profile", label: "Profil", icon: IdCard },
    { key: "enrollments", label: "Inscriptions", icon: GraduationCap },
    { key: "evaluations", label: "Évaluations", icon: ClipboardList },
    { key: "emargements", label: "Émargements", icon: FileSignature },
    { key: "payments", label: "Paiements", icon: CreditCard },
    { key: "documents", label: "Documents", icon: FileText },
    { key: "reclamations", label: "Réclamations", icon: MessageSquareWarning },
];

const EVAL_TYPES = [
    { key: "pre", label: "Pré" },
    { key: "post_acquis", label: "Post-acquis" },
    { key: "satisfaction_chaud", label: "Sat. chaud" },
    { key: "satisfaction_froid", label: "Sat. froid (J+30)" },
    { key: "transfert", label: "Transfert (J+90)" },
];

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtDateTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatusPill({ status }) {
    const map = {
        active: ["bg-green-100 text-green-700", "Actif"],
        disabled: ["bg-red-100 text-red-700", "Désactivé"],
        pending_onboarding: ["bg-orange-100 text-orange-700", "Onboarding"],
    };
    const [cls, label] = map[status] || ["bg-gray-100 text-gray-700", status || "—"];
    return <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${cls}`}>{label}</span>;
}

function EvalCell({ data }) {
    if (data?.submitted) {
        return (
            <div className="flex flex-col items-center gap-0.5 text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-[9px] font-semibold">{data.score != null ? `${data.score}%` : "Soumise"}</span>
            </div>
        );
    }
    if (data?.email_sent_at) {
        return (
            <div className="flex flex-col items-center gap-0.5 text-orange-600" title={`Email envoyé ${fmtDateTime(data.email_sent_at)}`}>
                <Mail className="h-4 w-4" />
                <span className="text-[9px] font-semibold">Pending</span>
            </div>
        );
    }
    if (data?.template_id) {
        return (
            <div className="flex flex-col items-center gap-0.5 text-gray-400">
                <Clock className="h-4 w-4" />
                <span className="text-[9px] font-semibold">À venir</span>
            </div>
        );
    }
    return <span className="text-gray-300">—</span>;
}

function ResponseDetailsDialog({ responseId, onClose }) {
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!responseId) return;
        api.get(`/admin/evaluations/responses/${responseId}/details`)
            .then((r) => setDoc(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [responseId]);
    if (!responseId) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="eval-details-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-3xl bg-white shadow-premium-lg flex flex-col"
            >
                <div className="flex items-start justify-between border-b border-brand-line p-6">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-orange">Réponse complète</div>
                        <h3 className="mt-1 font-display text-2xl">{doc?.template_title || "Chargement…"}</h3>
                        {doc && (
                            <div className="mt-2 text-xs text-brand-smoke">
                                Soumis {fmtDateTime(doc.submitted_at)} · Score : <strong className="text-brand-ink">{doc.score}%</strong>
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="text-brand-smoke hover:text-brand-ink">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-auto p-6 space-y-4">
                    {loading ? <div className="text-center text-brand-smoke">Chargement…</div> :
                     doc?.qa?.length ? doc.qa.map((qa, i) => (
                        <div key={i} className="rounded-xl border border-brand-line bg-[#FAFAFA] p-4">
                            <div className="text-[10px] uppercase tracking-wider text-brand-smoke">Question {i + 1} · {qa.type}</div>
                            <div className="mt-1 font-semibold text-brand-ink">{qa.question}</div>
                            <div className="mt-2 rounded-lg bg-white p-3 text-sm text-brand-ink">
                                {Array.isArray(qa.value) ? qa.value.join(", ") : (qa.value ?? <em className="text-brand-smoke">Sans réponse</em>)}
                            </div>
                        </div>
                     )) : <div className="text-center text-brand-smoke">Aucune réponse détaillée disponible.</div>}
                </div>
            </motion.div>
        </div>
    );
}

export default function AdminUserDossier() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tab, setTab] = useState("profile");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [responseDetailsId, setResponseDetailsId] = useState(null);
    const [notesValue, setNotesValue] = useState("");
    const [savingNotes, setSavingNotes] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/users/${id}/dossier`);
            setData(data);
            setNotesValue(data.profile?.admin_notes || "");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const counts = useMemo(() => ({
        enrollments: data?.enrollments?.length || 0,
        evaluations: data?.evaluations?.reduce((acc, ev) => acc + Object.values(ev.types).filter((t) => t.submitted).length, 0) || 0,
        emargements: data?.emargements?.length || 0,
        payments: data?.payments?.length || 0,
        documents: data?.documents?.length || 0,
        reclamations: data?.reclamations?.length || 0,
    }), [data]);

    const exportPdf = async () => {
        try {
            const token = localStorage.getItem("cursus_physio_token");
            const r = await fetch(`${API}/admin/users/${id}/dossier.pdf`, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error("Export PDF échoué");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `dossier-${(data?.profile?.last_name || "stagiaire").toLowerCase()}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Dossier PDF téléchargé");
        } catch (err) {
            toast.error(err.message);
        }
    };

    const saveNotes = async () => {
        setSavingNotes(true);
        try {
            await api.patch(`/admin/users/${id}/admin-notes`, { admin_notes: notesValue });
            toast.success("Notes admin enregistrées");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingNotes(false);
        }
    };

    if (loading || !data) {
        return <div className="mx-auto max-w-6xl px-6 py-10 text-brand-smoke" data-testid="dossier-loading">Chargement du dossier…</div>;
    }

    const p = data.profile;
    const fullName = `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.email;

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-admin-user-dossier">
            <div className="flex flex-wrap items-center gap-4">
                <button
                    onClick={() => navigate("/admin/utilisateurs")}
                    data-testid="dossier-back"
                    className="inline-flex items-center gap-1 text-sm text-brand-smoke hover:text-brand-ink"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Retour aux utilisateurs
                </button>
            </div>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">Dossier Qualiopi</div>
                    <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">{fullName}</h1>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-brand-smoke">
                        <span>{p.email}</span>
                        <span>·</span>
                        <StatusPill status={p.status} />
                        {p.is_demo_account && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-gray-600">Démo</span>
                        )}
                    </div>
                </div>
                <button
                    onClick={exportPdf}
                    data-testid="dossier-export-pdf"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c]"
                >
                    <FileDown className="h-3.5 w-3.5" /> Exporter le dossier complet
                </button>
            </div>

            <div className="mt-10 flex flex-col gap-6 md:flex-row">
                {/* Sidebar tabs */}
                <aside className="w-full md:w-64 flex-shrink-0">
                    <ul className="flex flex-row gap-2 overflow-x-auto md:flex-col" data-testid="dossier-tabs">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            const active = tab === t.key;
                            const count = counts[t.key];
                            return (
                                <li key={t.key}>
                                    <button
                                        onClick={() => setTab(t.key)}
                                        data-testid={`dossier-tab-${t.key}`}
                                        className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm transition ${
                                            active
                                                ? "bg-brand-orange text-white"
                                                : "bg-white border border-brand-line hover:border-brand-orange/40"
                                        }`}
                                    >
                                        <Icon className="h-4 w-4 flex-shrink-0" />
                                        <span className="flex-1 font-semibold">{t.label}</span>
                                        {typeof count === "number" && count > 0 && (
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] ${active ? "bg-white/20 text-white" : "bg-brand-line/40 text-brand-smoke"}`}>
                                                {count}
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </aside>

                {/* Tab content */}
                <main className="flex-1 min-w-0 rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid={`dossier-content-${tab}`}>
                    {tab === "profile" && (
                        <div className="space-y-6">
                            <div className="grid gap-4 md:grid-cols-2">
                                <Field label="Email" value={p.email} />
                                <Field label="Téléphone" value={p.phone || "—"} />
                                <Field label="ADELI / RPPS" value={p.adeli_rpps || "—"} />
                                <Field label="Adresse" value={p.address || "—"} />
                                <Field label="Inscrit depuis" value={fmtDate(p.created_at)} />
                                <Field label="Onboarding" value={p.onboarding_completed ? "Complété" : "En cours"} />
                            </div>
                            <div className="border-t border-brand-line pt-6">
                                <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Notes admin</label>
                                <textarea
                                    value={notesValue}
                                    onChange={(e) => setNotesValue(e.target.value)}
                                    rows={5}
                                    data-testid="dossier-admin-notes"
                                    className="mt-2 w-full rounded-xl border border-brand-line p-3 text-sm outline-none focus:border-brand-orange"
                                    placeholder="Notes internes (visibles admin uniquement)…"
                                />
                                <button
                                    onClick={saveNotes}
                                    disabled={savingNotes}
                                    data-testid="dossier-save-notes"
                                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-2 text-sm font-semibold text-white hover:bg-[#222]"
                                >
                                    {savingNotes ? "Enregistrement…" : "Enregistrer les notes"}
                                </button>
                            </div>
                        </div>
                    )}

                    {tab === "enrollments" && (
                        <div data-testid="dossier-enrollments-list">
                            {data.enrollments.length === 0 ? (
                                <Empty msg="Aucune inscription pour le moment." />
                            ) : (
                                <div className="space-y-3">
                                    {data.enrollments.map((e) => {
                                        const s = e.session || {};
                                        return (
                                            <div key={e.id} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-brand-line p-4">
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-semibold text-brand-ink">{s.formation_name || "Formation"}</div>
                                                    <div className="text-xs text-brand-smoke">
                                                        {fmtDate(s.start_date)} · {s.city || "?"} · {s.venue_name || "?"}
                                                    </div>
                                                    <div className="mt-1 text-xs text-brand-smoke">Formateur : {s.trainer_name || "—"}</div>
                                                </div>
                                                <div className="flex flex-col items-end gap-1 text-right">
                                                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                                                        e.status === "attended" ? "bg-green-100 text-green-700" :
                                                        e.status === "confirmed" ? "bg-blue-100 text-blue-700" :
                                                        e.status === "cancelled" ? "bg-red-100 text-red-700" :
                                                        "bg-gray-100 text-gray-700"
                                                    }`}>{e.status}</span>
                                                    <span className="text-xs text-brand-smoke">{e.payment_status} · {e.payment_amount_eur || 390}€</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "evaluations" && (
                        <div data-testid="dossier-evaluations-grid">
                            {data.evaluations.length === 0 ? (
                                <Empty msg="Aucune évaluation à afficher." />
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-xs">
                                        <thead>
                                            <tr className="border-b border-brand-line text-left">
                                                <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Formation · Date</th>
                                                {EVAL_TYPES.map((t) => (
                                                    <th key={t.key} className="px-3 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">{t.label}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-brand-line">
                                            {data.evaluations.map((ev) => (
                                                <tr key={ev.enrollment_id}>
                                                    <td className="px-3 py-3">
                                                        <div className="font-semibold text-brand-ink">{ev.formation_name}</div>
                                                        <div className="text-[10px] text-brand-smoke">{fmtDate(ev.session_start)}</div>
                                                    </td>
                                                    {EVAL_TYPES.map((t) => {
                                                        const cell = ev.types[t.key] || {};
                                                        return (
                                                            <td key={t.key} className="px-3 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => cell.response_id && setResponseDetailsId(cell.response_id)}
                                                                    disabled={!cell.response_id}
                                                                    data-testid={`eval-cell-${ev.enrollment_id}-${t.key}`}
                                                                    className={`mx-auto inline-flex items-center justify-center ${cell.response_id ? "cursor-pointer hover:scale-110" : "cursor-default"}`}
                                                                    title={cell.email_sent_at ? `Email envoyé le ${fmtDateTime(cell.email_sent_at)}` : ""}
                                                                >
                                                                    <EvalCell data={cell} />
                                                                </button>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className="mt-4 flex flex-wrap gap-3 text-[10px] text-brand-smoke">
                                        <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-green-600" /> Soumise</span>
                                        <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3 text-orange-500" /> Email envoyé · pending</span>
                                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-gray-400" /> À venir</span>
                                        <span className="ml-auto">Cliquez sur ✓ pour voir le détail des réponses.</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "emargements" && (
                        <div data-testid="dossier-emargements">
                            {data.emargements.length === 0 ? (
                                <Empty msg="Aucun émargement signé." />
                            ) : (
                                <div className="space-y-3">
                                    {data.emargements.map((em, i) => (
                                        <div key={i} className="rounded-xl border border-brand-line p-4">
                                            <div className="font-semibold text-brand-ink">{em.formation_name}</div>
                                            <div className="text-xs text-brand-smoke">{fmtDate(em.session_date)}</div>
                                            <div className="mt-2 flex flex-wrap gap-3 text-xs">
                                                <span className={`rounded-full px-2 py-0.5 ${em.morning?.signed ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                                                    Matin : {em.morning?.signed ? "✓ signé" : "non signé"}
                                                </span>
                                                <span className={`rounded-full px-2 py-0.5 ${em.afternoon?.signed ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                                                    Après-midi : {em.afternoon?.signed ? "✓ signé" : "non signé"}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "payments" && (
                        <div data-testid="dossier-payments">
                            {data.payments.length === 0 ? (
                                <Empty msg="Aucun paiement enregistré." />
                            ) : (
                                <>
                                <div className="mb-4 rounded-xl bg-brand-orange/10 p-4 text-sm">
                                    <span className="text-brand-smoke">Total dépensé : </span>
                                    <span className="font-display text-2xl font-medium text-brand-orange">{data.total_paid_eur || 0} €</span>
                                </div>
                                <div className="space-y-2">
                                    {data.payments.map((p) => (
                                        <div key={p.id || `${p.created_at}-${p.amount_eur}`} className="flex items-center justify-between gap-3 rounded-xl border border-brand-line p-4">
                                            <div>
                                                <div className="font-semibold text-brand-ink">{p.amount_eur || 0} €</div>
                                                <div className="text-xs text-brand-smoke">{fmtDateTime(p.created_at || p.updated_at)}</div>
                                            </div>
                                            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                                                p.status === "completed" ? "bg-green-100 text-green-700" :
                                                p.status === "pending" ? "bg-orange-100 text-orange-700" :
                                                "bg-gray-100 text-gray-700"
                                            }`}>{p.status}</span>
                                        </div>
                                    ))}
                                </div>
                                </>
                            )}
                        </div>
                    )}

                    {tab === "documents" && (
                        <div data-testid="dossier-documents">
                            {data.documents.length === 0 ? (
                                <Empty msg="Aucun document généré pour ce stagiaire." />
                            ) : (
                                <div className="space-y-2">
                                    {data.documents.map((d) => (
                                        <div key={d.log_id} className="flex items-center justify-between gap-3 rounded-xl border border-brand-line p-4">
                                            <div className="min-w-0">
                                                <div className="font-semibold text-brand-ink">{d.subject}</div>
                                                <div className="text-xs text-brand-smoke">
                                                    <code className="rounded bg-brand-line/30 px-1 py-0.5">{d.kind}</code>
                                                    <span className="ml-2">{fmtDateTime(d.sent_at)}</span>
                                                </div>
                                            </div>
                                            {d.has_attachment && d.attachment_filename && (
                                                <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700">📎 {d.attachment_filename}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "reclamations" && (
                        <div data-testid="dossier-reclamations">
                            {data.reclamations.length === 0 ? (
                                <Empty msg="Aucune réclamation déposée." />
                            ) : (
                                <div className="space-y-3">
                                    {data.reclamations.map((r) => (
                                        <div key={r.id} className="rounded-xl border border-brand-line p-4">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-[10px] font-semibold text-orange-700">{r.type}</span>
                                                <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[10px] font-semibold text-gray-700">{r.status}</span>
                                                <span className="text-xs text-brand-smoke">{fmtDateTime(r.created_at)}</span>
                                            </div>
                                            <p className="mt-2 text-sm text-brand-ink/80">{r.description}</p>
                                            {r.response_to_user && (
                                                <div className="mt-3 rounded-lg bg-[#FAFAFA] p-3 text-sm">
                                                    <div className="text-[10px] font-semibold uppercase text-brand-orange">Réponse Cursus Physio</div>
                                                    <p className="mt-1 whitespace-pre-line text-brand-ink/80">{r.response_to_user}</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>

            <ResponseDetailsDialog responseId={responseDetailsId} onClose={() => setResponseDetailsId(null)} />
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">{label}</div>
            <div className="mt-1 text-sm font-semibold text-brand-ink">{value}</div>
        </div>
    );
}

function Empty({ msg }) {
    return (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-sm text-brand-smoke">
            <AlertCircle className="h-6 w-6 text-brand-line" />
            {msg}
        </div>
    );
}
